<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

// Record types the backend audits (auditable_type values). Used to populate
// the type filter; labels resolve via AUDIT_LOGS.RECORD_TYPES.<key>.
const RECORD_TYPES = [
  'User',
  'Account',
  'Inbox',
  'Webhook',
  'Team',
  'TeamMember',
  'InboxMember',
  'Macro',
  'AccountUser',
  'AutomationRule',
  'Conversation',
];

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

// Local, unapplied filter state. Committed to the store on "Apply".
const filterForm = reactive({
  userId: '',
  auditableType: '',
  from: '',
  to: '',
});

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', { page });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const applyFilters = () => {
  store.dispatch('auditlogs/setFilters', {
    userId: filterForm.userId || null,
    auditableType: filterForm.auditableType || null,
    from: filterForm.from || null,
    to: filterForm.to || null,
  });
  // Reset to the first page. If we are already there, the page watcher won't
  // fire, so fetch explicitly; otherwise the route change triggers the fetch.
  if (routerPage.value !== 1) {
    router.push({ name: 'auditlogs_list', query: { page: 1 } });
  } else {
    fetchAuditLogs(1);
  }
};

const clearFilters = () => {
  filterForm.userId = '';
  filterForm.auditableType = '';
  filterForm.from = '';
  filterForm.to = '';
  applyFilters();
};

const exportCsv = async () => {
  if (!meta.value.totalEntries) {
    useAlert(t('AUDIT_LOGS.FILTERS.EXPORT_EMPTY'));
    return;
  }
  try {
    const blob = await store.dispatch('auditlogs/export');
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().slice(0, 10);
    link.download = `audit_logs_${today}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(error?.message || t('AUDIT_LOGS.FILTERS.EXPORT_ERROR'));
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page: page } });
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});

const inputClass =
  'h-9 px-3 rounded-lg bg-n-alpha-black2 text-n-slate-12 text-sm border border-n-weak focus:border-n-brand outline-none';
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('AUDIT_LOGS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUDIT_LOGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUDIT_LOGS.HEADER')"
        :description="$t('AUDIT_LOGS.DESCRIPTION')"
        :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
        feature-name="audit_logs"
      />
    </template>

    <!-- Filters live in preBody so they stay visible even when a filter
         returns no records (the body slot is hidden in that case). -->
    <template #preBody>
      <div class="flex flex-wrap items-end gap-3 mb-4">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUDIT_LOGS.FILTERS.ACTOR') }}
          </span>
          <select v-model="filterForm.userId" :class="inputClass">
            <option value="">{{ $t('AUDIT_LOGS.FILTERS.ALL_ACTORS') }}</option>
            <option
              v-for="agent in agentList"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUDIT_LOGS.FILTERS.TYPE') }}
          </span>
          <select v-model="filterForm.auditableType" :class="inputClass">
            <option value="">{{ $t('AUDIT_LOGS.FILTERS.ALL_TYPES') }}</option>
            <option v-for="type in RECORD_TYPES" :key="type" :value="type">
              {{ $t(`AUDIT_LOGS.RECORD_TYPES.${type}`) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUDIT_LOGS.FILTERS.FROM') }}
          </span>
          <input v-model="filterForm.from" type="date" :class="inputClass" />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('AUDIT_LOGS.FILTERS.TO') }}
          </span>
          <input v-model="filterForm.to" type="date" :class="inputClass" />
        </label>

        <button
          class="h-9 px-4 rounded-lg bg-n-brand text-white text-sm font-medium"
          @click="applyFilters"
        >
          {{ $t('AUDIT_LOGS.FILTERS.APPLY') }}
        </button>
        <button
          class="h-9 px-4 rounded-lg bg-n-alpha-black2 text-n-slate-12 text-sm font-medium border border-n-weak"
          @click="clearFilters"
        >
          {{ $t('AUDIT_LOGS.FILTERS.CLEAR') }}
        </button>
        <button
          class="h-9 px-4 ms-auto rounded-lg bg-n-alpha-black2 text-n-slate-12 text-sm font-medium border border-n-weak disabled:opacity-50"
          :disabled="uiFlags.exporting"
          @click="exportCsv"
        >
          {{
            uiFlags.exporting
              ? $t('AUDIT_LOGS.FILTERS.EXPORTING')
              : $t('AUDIT_LOGS.FILTERS.EXPORT')
          }}
        </button>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col">
        <BaseTable :headers="tableHeaders" :items="records">
          <template #row="{ items }">
            <BaseTableRow
              v-for="auditLogItem in items"
              :key="auditLogItem.id"
              :item="auditLogItem"
            >
              <template #default>
                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-12 whitespace-nowrap"
                  >
                    {{ generateLogText(auditLogItem) }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{
                      messageTimestamp(
                        auditLogItem.created_at,
                        'MMM dd, yyyy hh:mm a'
                      )
                    }}
                  </span>
                </BaseTableCell>

                <BaseTableCell class="w-36">
                  <span class="text-body-main text-n-slate-11">
                    {{ auditLogItem.remote_address }}
                  </span>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          :current-page="Number(meta.currentPage)"
          :total-items="meta.totalEntries"
          :items-per-page="meta.perPage"
          class="!px-0"
          @update:current-page="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
